import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import ProjectCard from '../components/ProjectCard';
import WebsiteCard from '../components/WebsiteCard';
import { projects } from '../data/projects';
import { websites } from '../data/websites';

const projectsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Nexsol Labs — Projects',
  url: 'https://nexsollabs.com/projects',
  hasPart: [
    ...projects.map((p) => ({
      '@type': 'CreativeWork',
      name: p.title,
      description: p.summary,
      url: `https://nexsollabs.com/projects/${p.slug}`,
    })),
    ...websites.map((w) => ({
      '@type': 'WebSite',
      name: w.title,
      description: w.summary,
      url: w.url,
    })),
  ],
};

const projectsBreadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nexsollabs.com/' },
    { '@type': 'ListItem', position: 2, name: 'Projects', item: 'https://nexsollabs.com/projects' },
  ],
};

export default function Projects() {
  return (
    <>
      <SEO
        title="Projects — Custom Software & Websites | Nexsol Labs"
        description="Work from Nexsol Labs — pharma SaaS (PharmaVision), recruitment platforms (NomadJobs.cloud), utility ERPs (MunicipalOS), invoicing & inventory (Fynvo), plus marketing websites and online stores. Custom software and sites shipped and in use across Macedonia and the Balkans."
        path="/projects"
        keywords="software case studies, PharmaVision, NomadJobs cloud, MunicipalOS, Fynvo, HotelOS, PharmacyOS, custom SaaS Skopje, pharma CRM Macedonia, utility ERP, recruitment software, изработка на веб страни Скопје, online store Macedonia, web design Skopje"
        jsonLd={[projectsJsonLd, projectsBreadcrumbJsonLd]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-page relative py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="eyebrow">Selected work</p>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-white md:text-5xl">
              Custom software,{' '}
              <span className="gradient-text">shipped and in use.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
              A representative sample of what we build. Industries vary; the principle
              doesn't — software designed around the actual workflow, owned by the team
              that runs it.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="container-page pb-20 md:pb-28">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((p, i) => (
            <ProjectCard key={p.slug} project={p} index={i} />
          ))}
        </div>

        {websites.length > 0 && (
          <div className="mt-20 md:mt-28">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <p className="eyebrow">Websites & online stores</p>
              <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
                Sites that look right and{' '}
                <span className="gradient-text">load fast.</span>
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
                Marketing websites, e-commerce platforms, and landing pages — designed,
                engineered, and shipped by the same team. Real projects too.
              </p>
            </motion.div>

            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {websites.map((w, i) => (
                <WebsiteCard key={w.slug} website={w} index={i} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center">
          <h2 className="font-display text-2xl font-semibold text-white md:text-3xl">
            Your project could be next.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/65">
            If you're sitting on a custom software idea — or stuck inside a tool that
            doesn't fit — we'd like to hear about it.
          </p>
          <div className="mt-6 flex justify-center">
            <Link to="/contact" className="btn-primary">
              Start a project
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
